import * as Sentry from '@sentry/nextjs'
import { useCallback, useRef } from 'react'

import { useFlag } from '@/feature/feature-flags'
import { reverseGeocode } from '@/feature/map'
import { usePersistentObservatoryPageStore } from '@/feature/observatory/store/usePersistentObservatoryPageStore'

/**
 * Writes a map position into the shared search location, then upgrades it with a
 * reverse-geocoded street address once that resolves.
 *
 * The coordinate is stored immediately so the UI never shows an empty address while the
 * lookup is in flight. `lastChanged` guards against a slow response from an earlier drag
 * overwriting a newer position — only the most recent request is allowed to commit.
 */
export const useReverseGeocodedPosition = () => {
  const setSearchLocation = usePersistentObservatoryPageStore((state) => state.setSearchLocation)

  const lastChanged = useRef(new Date())

  const reverseGeocodeMode = useFlag('reverseGeocodeMode', 'Google' as string)

  return useCallback((position: google.maps.LatLng) => {
    setSearchLocation({
      address: `${position.lat().toFixed(6)}, ${position.lng().toFixed(6)}`,
      location: position.toJSON(),
    })
    const updateTime = new Date()
    lastChanged.current = updateTime
    reverseGeocode(position.lat(), position.lng(), reverseGeocodeMode)
      .then((address) => {
        if (lastChanged.current === updateTime) {
          if (address) {
            setSearchLocation({
              address: address,
              location: position.toJSON(),
            })
          } else {
            setSearchLocation(undefined)
          }
        }
      })
      .catch((e) => Sentry.captureException(e))
  }, [])
}
